// Shared singleton state for the global ConfirmDialog. Any view can call
// `useConfirm().confirm({ title, ... })` and await the boolean result;
// `useConfirm().alert(...)` for a no-cancel acknowledgement; and
// `useConfirm().prompt(...)` for a single-line text input that returns
// the entered value (or null on cancel).
//
// This replaces every `window.confirm(...)`, `window.alert(...)` and
// `window.prompt(...)` in the app with a centered, branded modal that
// matches the rest of the UI (backdrop blur, rounded card, themed
// colors). One mount of <ConfirmDialog /> at the app root is enough —
// it pulls from this state.

import { ref } from 'vue'

export type ConfirmVariant = 'default' | 'warning' | 'destructive'
export type DialogMode = 'confirm' | 'alert' | 'prompt'

export interface DialogOptions {
  id: number
  mode: DialogMode
  title: string
  message?: string
  confirmLabel: string
  cancelLabel: string
  variant: ConfirmVariant
  dismissOnBackdrop?: boolean
  // Prompt-only
  placeholder?: string
  defaultValue?: string
  inputType?: 'text' | 'password' | 'email'
  required?: boolean
}

// Kept named `confirmState` for backward compat with existing imports.
export const confirmState = ref<DialogOptions | null>(null)

let nextId = 1
let resolver: ((value: unknown) => void) | null = null

export function resolveConfirm(value: boolean | string | null) {
  const r = resolver
  resolver = null
  confirmState.value = null
  r?.(value)
}

interface ConfirmInput {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  dismissOnBackdrop?: boolean
}

interface PromptInput extends ConfirmInput {
  placeholder?: string
  defaultValue?: string
  inputType?: 'text' | 'password' | 'email'
  required?: boolean
}

function openConfirmLike(input: ConfirmInput, mode: 'confirm' | 'alert'): Promise<boolean> {
  // If a dialog is already open, resolve it as cancelled before showing
  // the next one — UX-wise it's better than queueing or stomping silently.
  if (resolver) resolveConfirm(false)
  return new Promise<boolean>((resolve) => {
    resolver = resolve as (v: unknown) => void
    confirmState.value = {
      id: nextId++,
      mode,
      title: input.title,
      message: input.message,
      confirmLabel: input.confirmLabel ?? (mode === 'alert' ? 'OK' : 'Confirm'),
      cancelLabel: input.cancelLabel ?? 'Cancel',
      variant: input.variant ?? 'default',
      dismissOnBackdrop: input.dismissOnBackdrop,
    }
  })
}

function openPrompt(input: PromptInput): Promise<string | null> {
  if (resolver) resolveConfirm(null)
  return new Promise<string | null>((resolve) => {
    resolver = resolve as (v: unknown) => void
    confirmState.value = {
      id: nextId++,
      mode: 'prompt',
      title: input.title,
      message: input.message,
      confirmLabel: input.confirmLabel ?? 'OK',
      cancelLabel: input.cancelLabel ?? 'Cancel',
      variant: input.variant ?? 'default',
      dismissOnBackdrop: input.dismissOnBackdrop,
      placeholder: input.placeholder,
      defaultValue: input.defaultValue ?? '',
      inputType: input.inputType ?? 'text',
      required: input.required ?? false,
    }
  })
}

export function useConfirm() {
  return {
    confirm: (input: ConfirmInput) => openConfirmLike(input, 'confirm'),
    alert: (input: ConfirmInput) => openConfirmLike(input, 'alert'),
    prompt: (input: PromptInput) => openPrompt(input),
  }
}
